import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useAppStore } from '@/modules/apps/store';
import { itemService } from '@/services/item';
import useVuelidate from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

export function useSysItemList() {
    const appStore = useAppStore();

    const selectedFeature = ref(1);
    const { appSelectOptionsWithAll } = storeToRefs(appStore);

    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            forAppId: { type: 'number', default: -1 },
            name: { type: 'string', default: '' },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const v$ = useVuelidate({ name: { minLength: minLength(3) } }, query);

    const { list, total, fetchList } = useBaseListView({
        request: itemService.item.list,
        paramsBuilder: () => ({
            ...query,
            forAppId: query.forAppId === -1 ? undefined : query.forAppId // fetch all items if -1
        }),
        hooks: {
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            canFetch: () => {
                v$.value.$touch();
                return !v$.value.$invalid;
            },
            transformResponse: (list: any[]) =>
                list.map((item: any) => ({
                    ...item,
                    curAppNames: appStore.getAppNamesByIds(item.curAppIds),
                    nextAppNames: appStore.getAppNamesByIds(item.nextAppIds)
                }))
        }
    });

    const actions = {
        remove: useCrudActions().confirmAndRemove({
            request: itemService.item.remove,
            onSuccess: () => fetchList()
        })
    };

    return { selectedFeature, query, v$, list, total, fetchList, actions, appSelectOptionsWithAll };
}
